import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { notification } from 'antd';

import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import {
  AccountPlan,
  AccountPlanResponse,
  EditTarget,
} from '@totem/components/accountPlan/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ACCOUNT_PLAN_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  children: ReactNode;
};

const readError = (res: Response) =>
  res.text().then((text) => {
    throw new Error(text || res.statusText);
  });

const AccountPlanContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [data, setData] = useState<AccountPlanResponse | null>(null);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);

    fetch(ACCOUNT_PLAN_ENDPOINT, {
      headers: { Authorization: `Bearer ${getApiToken()}` },
    })
      .then((res) => (res.ok ? res.json() : readError(res)))
      .then((result: AccountPlanResponse) => setData(result))
      .catch(() =>
        notification.error({ message: 'Failed to load the account plan.' }),
      )
      .finally(() => setLoading(false));
  }, [getApiToken]);

  // Reload whenever the user switches organization — the plan always targets the active org.
  useEffect(() => {
    setData(null);

    refresh();
  }, [userProfile.organization.id, refresh]);

  const savePlan = (plan: AccountPlan) => {
    setSaving(true);

    return fetch(ACCOUNT_PLAN_ENDPOINT, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getApiToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(plan),
    })
      .then((res) => (res.ok ? res.json() : readError(res)))
      .then((result: AccountPlanResponse) => {
        setData(result);

        return true;
      })
      .catch((err: Error) => {
        notification.error({
          message: 'Failed to save the account plan.',
          description: err.message,
        });

        return false;
      })
      .finally(() => setSaving(false));
  };

  const generateNarrative = () => {
    setGenerating(true);

    fetch(`${ACCOUNT_PLAN_ENDPOINT}/narrative`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getApiToken()}` },
    })
      .then((res) => (res.ok ? res.json() : readError(res)))
      .then((result: AccountPlanResponse) => setData(result))
      .catch(() =>
        notification.error({ message: 'Failed to generate the narrative.' }),
      )
      .finally(() => setGenerating(false));
  };

  return (
    <AccountPlanContext.Provider
      value={{
        loading,
        saving,
        generating,
        data,
        onRefresh: refresh,
        savePlan,
        generateNarrative,
        editTarget,
        openEditor: setEditTarget,
        closeEditor: () => setEditTarget(null),
      }}
    >
      {children}
    </AccountPlanContext.Provider>
  );
};

export default AccountPlanContainer;
